'use client'

import { useState, useEffect } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { OperatingCost, OperatingCostInput, OperatingCostType } from '@garage/shared'
import {
  operatingCostInputSchema,
  OPERATING_COST_TYPES,
  OPERATING_COST_TYPE_LABELS,
  parseMoneyToMinor,
} from '@garage/shared'
import { Dialog, TextField, Select, Switch, Button, useSnackbar } from '@/ui'
import { api, ApiError } from '@/lib/api.js'
import { qk } from '@/lib/query-keys.js'

interface OperatingCostFormDialogProps {
  open: boolean
  cost?: OperatingCost
  onClose: () => void
}

interface FormState {
  type: OperatingCostType
  description: string
  date: string
  amountBeforeVatStr: string
  vatStr: string
  recurringMonthly: boolean
  endsOn: string | null
  paidAt: string | null
  notes: string | null
}

function today(): string {
  return new Date().toISOString().slice(0, 10)
}

export function OperatingCostFormDialog({ open, cost, onClose }: OperatingCostFormDialogProps) {
  const queryClient = useQueryClient()
  const snackbar = useSnackbar()
  const { t } = useTranslation()

  const isCreate = !cost

  const initialState = (): FormState => ({
    type: cost?.type ?? 'other',
    description: cost?.description ?? '',
    date: cost?.date ?? today(),
    amountBeforeVatStr: cost ? String(cost.amountBeforeVatMinor / 100) : '',
    vatStr: cost ? String(cost.vatMinor / 100) : '',
    recurringMonthly: cost?.recurringMonthly ?? false,
    endsOn: cost?.endsOn ?? null,
    paidAt: cost?.paidAt ?? null,
    notes: cost?.notes ?? null,
  })

  const [formState, setFormState] = useState<FormState>(initialState)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (open) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setFormState(initialState())
      setFieldErrors({})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, cost])

  const mutation = useMutation({
    mutationFn: async (input: OperatingCostInput) =>
      isCreate ? api.operatingCosts.create(input) : api.operatingCosts.update(cost!.id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.operatingCosts({}) })
      queryClient.invalidateQueries({ queryKey: ['finance-advanced'] })
      snackbar.success(isCreate ? t('accounting.advanced.costAdded') : t('accounting.advanced.costUpdated'))
      onClose()
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        setFieldErrors(error.fieldErrors)
        snackbar.error(error.message)
      } else {
        snackbar.error(t('common.errorOccurred'))
      }
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    const input: OperatingCostInput = {
      type: formState.type,
      description: formState.description,
      date: formState.date,
      amountBeforeVatMinor: parseMoneyToMinor(formState.amountBeforeVatStr) ?? 0,
      vatMinor: parseMoneyToMinor(formState.vatStr) ?? 0,
      recurringMonthly: formState.recurringMonthly,
      endsOn: formState.recurringMonthly && formState.endsOn ? formState.endsOn : null,
      paidAt: formState.paidAt || null,
      notes: formState.notes || null,
    }

    const result = operatingCostInputSchema.safeParse(input)
    if (!result.success) {
      const errors: Record<string, string> = {}
      result.error.issues.forEach((issue) => {
        errors[issue.path.join('.')] = issue.message
      })
      setFieldErrors(errors)
      return
    }

    setFieldErrors({})
    mutation.mutate(input)
  }

  const typeOptions = OPERATING_COST_TYPES.map((t) => ({
    value: t,
    label: t === 'other' ? 'Other' : OPERATING_COST_TYPE_LABELS[t],
  }))

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={isCreate ? t('accounting.advanced.addCost') : t('accounting.advanced.editCost')}
      size="sm"
      dismissible={false}
      actions={
        <div className="flex gap-3 justify-end">
          <Button variant="outlined" onClick={onClose} disabled={mutation.isPending}>
            {t('common.cancel')}
          </Button>
          <Button variant="filled" onClick={handleSubmit} loading={mutation.isPending}>
            {isCreate ? t('common.add') : t('common.save')}
          </Button>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Select
          label={t('accounting.advanced.type')}
          options={typeOptions}
          value={formState.type}
          onChange={(value) => setFormState((s) => ({ ...s, type: value as OperatingCostType }))}
          error={fieldErrors.type}
          fullWidth
        />

        <TextField
          label={t('accounting.advanced.description')}
          value={formState.description}
          onChange={(v) => setFormState((s) => ({ ...s, description: v }))}
          error={fieldErrors.description}
          fullWidth
          autoFocus
        />

        <TextField
          label={t('calendar.date')}
          type="date"
          value={formState.date}
          onChange={(v) => setFormState((s) => ({ ...s, date: v }))}
          error={fieldErrors.date}
          fullWidth
        />

        <div className="grid grid-cols-2 gap-3">
          <TextField
            label={t('accounting.beforeVat')}
            value={formState.amountBeforeVatStr}
            onChange={(v) => setFormState((s) => ({ ...s, amountBeforeVatStr: v }))}
            error={fieldErrors.amountBeforeVatMinor}
            numeric
            fullWidth
          />
          <TextField
            label={t('accounting.vat')}
            value={formState.vatStr}
            onChange={(v) => setFormState((s) => ({ ...s, vatStr: v }))}
            error={fieldErrors.vatMinor}
            numeric
            fullWidth
          />
        </div>

        <div className="pt-2 border-t border-outline-variant">
          <Switch
            label={t('accounting.advanced.recurringMonthly')}
            checked={formState.recurringMonthly}
            onChange={(checked) => setFormState((s) => ({ ...s, recurringMonthly: checked }))}
          />
        </div>

        {formState.recurringMonthly && (
          <>
            <TextField
              label={t('accounting.advanced.endsOn')}
              type="date"
              value={formState.endsOn ?? ''}
              onChange={(v) => setFormState((s) => ({ ...s, endsOn: v || null }))}
              error={fieldErrors.endsOn}
              fullWidth
            />
            <p className="text-body-xs text-on-surface-variant">{t('accounting.advanced.endsOnHelp')}</p>
          </>
        )}

        <TextField
          label={t('accounting.advanced.paidAt')}
          type="date"
          value={formState.paidAt ?? ''}
          onChange={(v) => setFormState((s) => ({ ...s, paidAt: v || null }))}
          error={fieldErrors.paidAt}
          fullWidth
        />

        <TextField
          label={t('clientDetail.notes')}
          value={formState.notes ?? ''}
          onChange={(v) => setFormState((s) => ({ ...s, notes: v || null }))}
          error={fieldErrors.notes}
          multiline
          rows={3}
          fullWidth
        />
      </form>
    </Dialog>
  )
}
